import React, { useEffect, useRef, useState, useMemo } from "react";
import dayjs from "dayjs";
import html2canvas from 'html2canvas';
import {
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { useChartPalette } from "../Tailwind/chartColors";

export default function LineAndBarCombinedChartItem({ name, data_x, data_y_line, data_y_bar, data_y_line_name, data_y_bar_name }) {
    const [clipBoardLoading, setClipBoardLoading] = useState(false);
    const graphRef = useRef(null);

    useEffect(() => {
        if (clipBoardLoading && graphRef.current) {
            html2canvas(graphRef.current, {
                onclone: (clonedDoc, clonedEl) => {
                    const btn = clonedEl.querySelector('[data-copy-btn]');
                    if (btn) btn.remove();
                    const title = clonedEl.querySelector('h3');
                    if (title) title.style.paddingBottom = '4px';
                },
            }).then(canvas => {
                canvas.toBlob(blob => {
                    const item = new ClipboardItem({ 'image/png': blob });
                    navigator.clipboard.write([item]);
                    setClipBoardLoading(false);
                });
            });
        }
    }, [clipBoardLoading]);

    const handleCopy = () => {
        window.focus();
        setClipBoardLoading(true);
    };

    // Transform to recharts row-based format
    const chartData = useMemo(() => {
        return data_x.data.map((xValue, i) => ({
            x: dayjs(xValue).year().toString(),
            [data_y_line_name]: data_y_line.data[i],
            [data_y_bar_name]: data_y_bar.data[i],
        }));
    }, [data_x, data_y_line, data_y_bar, data_y_line_name, data_y_bar_name]);

    const palette = useChartPalette();
    const lineColor = palette[0];
    const barColor = palette[1 % palette.length];

    return (
        <div className="min-w-0">
            <div ref={graphRef} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm overflow-hidden min-w-0">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-900 truncate">{name}</h3>
                    <button
                        data-copy-btn
                        onClick={handleCopy}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors group relative flex-shrink-0"
                        title="Copy to clipboard"
                    >
                        {!clipBoardLoading ? (
                            <span className="material-symbols-outlined text-[20px] text-slate-500">content_copy</span>
                        ) : (
                            <div className="w-5 h-5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                        )}
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Copy to clipboard
                        </span>
                    </button>
                </div>
                <div className="min-w-0 w-full">
                    <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e2e8f0"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="x"
                                tick={{ fill: '#64748b', fontSize: 12 }}
                                stroke="#cbd5e1"
                            />
                            <YAxis
                                yAxisId="left"
                                tick={{ fill: '#64748b', fontSize: 12 }}
                                stroke="#cbd5e1"
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fill: '#64748b', fontSize: 12 }}
                                stroke="#cbd5e1"
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '0.5rem',
                                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                                }}
                                labelStyle={{ color: '#1e293b', fontWeight: 600 }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '20px' }} />
                            <Bar
                                yAxisId="left"
                                dataKey={data_y_bar_name}
                                fill={barColor}
                                name={data_y_bar_name}
                            />
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey={data_y_line_name}
                                stroke={lineColor}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                                name={data_y_line_name}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
